import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Chip, Divider, Grid, Stack, Typography } from '@mui/material';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import PublicLayout from '../components/PublicLayout.jsx';
import { resolveShopContext } from '../common/shopContext';
import { getSiteTheme, normalizeTemplateKey } from '../common/websiteTemplates';
import { addToCart } from '../common/shopUtils';
import { formatMoney, getEffectiveProductPricing } from '../common/shopPricing';
import { isPartnerComingSoon, isPartnerMaintenance } from '../common/partnerUtils';
import PartnerUpsellPage from './PartnerUpsellPage';
import PartnerMaintenancePage from './PartnerMaintenancePage';
import PartnerComingSoonPage from './PartnerComingSoonPage';
import { getLocalizedShopItem } from '../common/shopContent';

const stripHtml = (value) => String(value || '').replace(/<[^>]*>/g, '').trim();

export default function ShopProduct() {
  const { partnerSlug, productId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useSelector((state) => state.auth);
  const settings = useSelector((state) => state.settingsdata.settings);
  const { t, i18n } = useTranslation();

  const shopContext = useMemo(
    () =>
      resolveShopContext(settings, {
        partnerSlug,
        locale: i18n.language,
        pathname: location.pathname,
      }),
    [settings, partnerSlug, i18n.language, location.pathname]
  );

  const { partner, cartScopeKey, paths, websiteContent } = shopContext;
  const isAdmin = auth?.profile?.usertype === 'admin' || auth?.profile?.usertype === 'fleetadmin';

  const templateKey = normalizeTemplateKey(websiteContent.template || 'classic');
  const theme = getSiteTheme(templateKey, websiteContent.appearance || {});
  const shop = websiteContent.shop || {};
  const shopEnabled = shop.enabled !== false;
  const currency = shop.currency || 'USD';
  const campaigns = Array.isArray(shop.campaigns) ? shop.campaigns : [];
  const products = Array.isArray(shop.products) ? shop.products : [];
  const catalogCategories = useMemo(() => {
    const list = Array.isArray(shop.categories) ? shop.categories : [];
    return list
      .filter((cat) => cat && cat.published !== false && cat.id)
      .map((cat) => getLocalizedShopItem(cat, i18n.language));
  }, [shop.categories, i18n.language]);

  const product = products.find((p) => p && (p.id === productId || p.slug === productId));
  const localizedProduct = useMemo(() => getLocalizedShopItem(product, i18n.language), [product, i18n.language]);
  const categoryLabel = useMemo(() => {
    const key = String(product?.category || localizedProduct?.category || '').trim();
    if (!key) return '';
    const match = catalogCategories.find((cat) => String(cat?.id || '') === key);
    return match?.title || key;
  }, [product?.category, localizedProduct?.category, catalogCategories]);
  const pricing = getEffectiveProductPricing(product, campaigns);
  const isService = localizedProduct?.type === 'service';
  const stock = Number(localizedProduct?.stock || 0);
  const isOutOfStock = !isService && localizedProduct && Number.isFinite(stock) && stock <= 0;

  const galleryImages = useMemo(() => {
    const list = [];
    const main = localizedProduct?.image || localizedProduct?.thumbnail;
    if (main) list.push(main);
    const extra = Array.isArray(localizedProduct?.images) ? localizedProduct.images : [];
    extra.forEach((url) => {
      if (url && !list.includes(url)) list.push(url);
    });
    return list;
  }, [localizedProduct]);

  const [activeImage, setActiveImage] = useState('');
  useEffect(() => {
    setActiveImage(galleryImages[0] || '');
  }, [galleryImages]);

  const handlePrimary = () => {
    if (!localizedProduct?.id) return;
    if (localizedProduct.externalLink) {
      window.open(localizedProduct.externalLink, '_blank', 'noopener,noreferrer');
      return;
    }
    if (isOutOfStock) return;
    addToCart(localizedProduct.id, 1, cartScopeKey);
    navigate(paths.cart);
  };

  if (partner?.inactive) {
    return <PartnerUpsellPage partner={partner} />;
  }

  if (partner && isPartnerComingSoon(partner) && !isAdmin) {
    return <PartnerComingSoonPage partner={partner} />;
  }

  if (partner && isPartnerMaintenance(partner) && !isAdmin) {
    return <PartnerMaintenancePage partner={partner} />;
  }

  if (!shopEnabled) {
    return (
      <PublicLayout title={t('shop') || 'Shop'} partner={partner} homePath={paths.home} bookingPath={paths.booking}>
        <Box sx={{ maxWidth: 1100, mx: 'auto', px: { xs: 2, md: 4 }, py: { xs: 6, md: 8 } }}>
          <Typography sx={{ fontWeight: 900, color: theme.text, fontSize: 28 }}>
            {t('shop_disabled') || 'Shop is disabled'}
          </Typography>
          <Typography sx={{ color: theme.muted, mt: 1 }}>
            {t('shop_disabled_help') || 'Please check back later.'}
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate(paths.home)}
            sx={{ mt: 3, borderRadius: 2, textTransform: 'none', fontWeight: 800 }}
          >
            {t('home') || 'Home'}
          </Button>
        </Box>
      </PublicLayout>
    );
  }

  if (!product) {
    return (
      <PublicLayout title={t('shop') || 'Shop'} partner={partner} homePath={paths.home} bookingPath={paths.booking}>
        <Box sx={{ maxWidth: 1100, mx: 'auto', px: { xs: 2, md: 4 }, py: { xs: 6, md: 8 } }}>
          <Typography sx={{ fontWeight: 900, color: theme.text, fontSize: 28 }}>
            {t('not_found') || 'Not found'}
          </Typography>
          <Typography sx={{ color: theme.muted, mt: 1 }}>
            {t('product_not_found') || 'This product does not exist (or is unpublished).'}
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate(paths.shop)}
            sx={{ mt: 3, borderRadius: 2, textTransform: 'none', fontWeight: 800 }}
          >
            {t('back_to_shop') || 'Back to shop'}
          </Button>
        </Box>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout
      title={localizedProduct?.seo?.title || localizedProduct?.title}
      description={localizedProduct?.seo?.description || stripHtml(localizedProduct?.description || '')}
      partner={partner}
      homePath={paths.home}
      bookingPath={paths.booking}
    >
      <Box sx={{ maxWidth: 1100, mx: 'auto', px: { xs: 2, md: 4 }, py: { xs: 6, md: 8 } }}>
        <Button
          variant="outlined"
          onClick={() => navigate(paths.shop)}
          sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 800, mb: 3 }}
        >
          {t('back') || 'Back'}
        </Button>

        <Grid container spacing={4} alignItems="flex-start">
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                borderRadius: 3,
                overflow: 'hidden',
                border: `1px solid ${theme.border}`,
                bgcolor: theme.sectionAltBg,
                height: { xs: 320, md: 440 },
                position: 'relative',
              }}
            >
              {activeImage ? (
                <Box
                  component="img"
                  src={activeImage}
                  alt={localizedProduct?.title}
                  sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              ) : (
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    background:
                      'radial-gradient(900px 360px at 25% 30%, rgba(56,189,248,0.35), transparent 60%), radial-gradient(800px 360px at 85% 30%, rgba(34,197,94,0.25), transparent 60%)',
                  }}
                />
                )}
            </Box>

            {galleryImages.length > 1 ? (
              <Stack direction="row" spacing={1} sx={{ mt: 2, overflowX: 'auto', pb: 1 }}>
                {galleryImages.map((url) => {
                  const selected = url === activeImage;
                  return (
                    <Box
                      key={url}
                      component="button"
                      type="button"
                      onClick={() => setActiveImage(url)}
                      style={{ all: 'unset', cursor: 'pointer' }}
                    >
                      <Box
                        component="img"
                        src={url}
                        alt=""
                        sx={{
                          width: 84,
                          height: 64,
                          objectFit: 'cover',
                          borderRadius: 2,
                          border: selected ? `2px solid ${theme.accent || '#111827'}` : `1px solid ${theme.border}`,
                          boxShadow: selected ? '0 10px 22px rgba(15,23,42,0.18)' : 'none',
                        }}
                      />
                    </Box>
                  );
                })}
              </Stack>
            ) : null}
          </Grid>

          <Grid item xs={12} md={6}>
            <Stack spacing={2}>
              <Box>
                <Typography sx={{ fontWeight: 900, color: theme.text, fontSize: { xs: 30, md: 40 }, letterSpacing: -1 }}>
                  {localizedProduct?.title}
                </Typography>
                {categoryLabel ? (
                  <Chip
                    size="small"
                    label={categoryLabel}
                    sx={{ mt: 1, fontWeight: 900 }}
                    variant="outlined"
                  />
                ) : null}
                {isService ? (
                  <Chip size="small" label={t('service') || 'Service'} sx={{ mt: 1, ml: 1, fontWeight: 900 }} />
                ) : null}
                {pricing?.badgeText ? (
                  <Chip
                    size="small"
                    label={pricing.badgeText}
                    sx={{ mt: 1, ml: 1, fontWeight: 900, bgcolor: theme.accent || '#111827', color: '#fff' }}
                  />
                ) : null}
                {isOutOfStock ? (
                  <Chip
                    size="small"
                    label={t('out_of_stock') || 'Out of stock'}
                    sx={{ mt: 1, ml: 1, fontWeight: 900 }}
                    color="warning"
                    variant="outlined"
                  />
                ) : null}
              </Box>

              {localizedProduct?.description ? (
                <Typography
                  component="div"
                  sx={{ color: theme.muted, fontSize: 16, lineHeight: 1.7 }}
                  dangerouslySetInnerHTML={{ __html: localizedProduct.description }}
                />
              ) : null}

              <Divider />

              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography sx={{ color: theme.muted }}>{t('price') || 'Price'}</Typography>
                <Stack direction="row" spacing={1} alignItems="baseline">
                  {pricing?.compareAtPrice ? (
                    <Typography sx={{ color: theme.muted, fontWeight: 800, textDecoration: 'line-through' }}>
                      {formatMoney(pricing.compareAtPrice, currency)}
                    </Typography>
                  ) : null}
                  <Typography sx={{ fontWeight: 900, color: theme.text, fontSize: 20 }}>
                    {formatMoney(pricing?.finalPrice ?? product.price, currency)}
                  </Typography>
                </Stack>
              </Stack>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 1 }}>
                <Button
                  variant="contained"
                  onClick={handlePrimary}
                  disabled={Boolean(isOutOfStock) || !localizedProduct?.id}
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 900,
                    bgcolor: theme.accent || '#111827',
                    '&:hover': { bgcolor: theme.accent || '#0b1224' },
                  }}
                >
                  {localizedProduct?.externalLink
                    ? (localizedProduct.externalLinkText || t('view') || 'View')
                    : (t('add_to_cart') || 'Add to cart')}
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => navigate(paths.cart)}
                  sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 900 }}
                >
                  {t('view_cart') || 'View cart'}
                </Button>
              </Stack>
            </Stack>
          </Grid>
        </Grid>
      </Box>
    </PublicLayout>
  );
}
